<template>
  <div
    class="home-hot-deals"
    @mouseenter="pauseAutoScroll"
    @mouseleave="resumeAutoScroll"
  >
    <p v-if="hintText" class="home-hot-deals__hint">{{ hintText }}</p>

    <div
      v-if="loading"
      class="home-hot-deals__viewport home-hot-deals__viewport--loading"
      dir="ltr"
    >
      <div class="home-hot-deals__track">
        <div
          v-for="n in skeletonCount"
          :key="n"
          class="home-hot-deals__slide home-hot-deals__skeleton"
        />
      </div>
    </div>

    <div
      v-else-if="!products.length"
      class="home-hot-deals__empty"
    >
      {{ $l('homeHotDealsEmpty') }}
    </div>

    <div
      v-else
      ref="viewport"
      class="home-hot-deals__viewport"
      dir="ltr"
      :class="{ 'home-hot-deals__viewport--dragging': isDragging }"
      @mousedown="onDragStart"
      @mousemove="onDragMove"
      @mouseup="onDragEnd"
      @mouseleave="onDragEnd"
      @touchstart.passive="onTouchStart"
      @touchmove.passive="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
      @scroll="onUserScroll"
    >
      <div ref="track" class="home-hot-deals__track">
        <div
          v-for="(product, index) in displayProducts"
          :key="`${cardProps(product).productKey}-${index}`"
          class="home-hot-deals__slide"
        >
          <shopping-item-card
            v-bind="cardProps(product)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ShoppingItemCard from './ShoppingItemCard.vue';
import { getProductKey } from '@/utils/priceUtils';

const MAX_POLL_ATTEMPTS = 24;
const POLL_INTERVAL_MS = 3000;
const AUTO_SCROLL_PX_PER_FRAME = 0.45;
const USER_SCROLL_PAUSE_MS = 2500;

export default {
  name: 'HomeHotDeals',
  components: {
    ShoppingItemCard,
  },
  props: {
    country: {
      type: String,
      default: 'IL',
    },
    currency: {
      type: String,
      default: 'NIS',
    },
    currencySign: {
      type: String,
      default: '₪',
    },
    poolSize: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      loading: true,
      products: [],
      cachedPoolSize: 0,
      builtAt: null,
      pollTimer: null,
      pollAttempts: 0,
      rafId: null,
      autoScrollPaused: false,
      autoScrollDirection: 1,
      isDragging: false,
      dragStartX: 0,
      dragScrollLeft: 0,
      touchStartX: null,
      userScrollTimer: null,
    };
  },
  computed: {
    skeletonCount() {
      return Math.min(this.poolSize, 4);
    },
    displayProducts() {
      if (!this.products.length) {
        return [];
      }
      return this.products.slice(0, this.poolSize);
    },
    hintText() {
      if (this.loading) {
        return this.$l('homeHotDealsLoading');
      }
      if (this.builtAt) {
        return this.$l('homeHotDealsHint');
      }
      return '';
    },
  },
  watch: {
    country() {
      this.reload();
    },
    currency() {
      this.reload();
    },
    products(next) {
      if (next.length) {
        this.$nextTick(() => {
          this.resetScrollPosition();
          this.startAutoScroll();
        });
      }
    },
  },
  mounted() {
    this.fetchHotDeals();
  },
  beforeDestroy() {
    this.stopAutoScroll();
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
    if (this.userScrollTimer) {
      clearTimeout(this.userScrollTimer);
    }
  },
  methods: {
    reload() {
      this.stopAutoScroll();
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      this.pollAttempts = 0;
      this.loading = true;
      this.products = [];
      this.fetchHotDeals();
    },
    cardProps(product) {
      const key = getProductKey(product);
      return {
        itemTitle: product.otherStores[0].title,
        itemImageUrl: product.otherStores[0].image.imageUrl,
        currencySign: this.currencySign,
        items: product.otherStores,
        itemType: product.itemType,
        isBestDeal: false,
        isCarousel: true,
        storesCount: 3,
        shipToCountry: this.country,
        productKey: key,
        searchQuery: product.searchQuery || '',
      };
    },
    resetScrollPosition() {
      const viewport = this.$refs.viewport;
      if (!viewport) {
        return;
      }
      viewport.scrollLeft = 0;
      this.autoScrollDirection = 1;
    },
    getMaxScrollLeft() {
      const viewport = this.$refs.viewport;
      if (!viewport) {
        return 0;
      }
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    },
    clampScrollPosition() {
      const viewport = this.$refs.viewport;
      if (!viewport) {
        return;
      }
      const maxScroll = this.getMaxScrollLeft();
      if (viewport.scrollLeft < 0) {
        viewport.scrollLeft = 0;
      } else if (viewport.scrollLeft > maxScroll) {
        viewport.scrollLeft = maxScroll;
      }
    },
    startAutoScroll() {
      this.stopAutoScroll();
      const tick = () => {
        const viewport = this.$refs.viewport;
        if (
          viewport
          && !this.autoScrollPaused
          && !this.isDragging
          && this.displayProducts.length > 1
        ) {
          const maxScroll = this.getMaxScrollLeft();
          if (maxScroll > 0) {
            viewport.scrollLeft += AUTO_SCROLL_PX_PER_FRAME * this.autoScrollDirection;
            if (viewport.scrollLeft >= maxScroll) {
              viewport.scrollLeft = maxScroll;
              this.autoScrollDirection = -1;
            } else if (viewport.scrollLeft <= 0) {
              viewport.scrollLeft = 0;
              this.autoScrollDirection = 1;
            }
          }
        }
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    },
    stopAutoScroll() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },
    pauseAutoScroll() {
      this.autoScrollPaused = true;
    },
    resumeAutoScroll() {
      if (!this.isDragging) {
        this.autoScrollPaused = false;
      }
    },
    pauseAutoScrollTemporarily() {
      this.autoScrollPaused = true;
      if (this.userScrollTimer) {
        clearTimeout(this.userScrollTimer);
      }
      this.userScrollTimer = setTimeout(() => {
        if (!this.isDragging) {
          this.autoScrollPaused = false;
        }
      }, USER_SCROLL_PAUSE_MS);
    },
    onUserScroll() {
      if (this.isDragging) {
        return;
      }
      this.clampScrollPosition();
      this.pauseAutoScrollTemporarily();
    },
    onDragStart(event) {
      if (event.button !== 0) {
        return;
      }
      const viewport = this.$refs.viewport;
      if (!viewport) {
        return;
      }
      this.isDragging = true;
      this.autoScrollPaused = true;
      this.dragStartX = event.clientX;
      this.dragScrollLeft = viewport.scrollLeft;
      event.preventDefault();
    },
    onDragMove(event) {
      if (!this.isDragging) {
        return;
      }
      const viewport = this.$refs.viewport;
      if (!viewport) {
        return;
      }
      const delta = event.clientX - this.dragStartX;
      viewport.scrollLeft = this.dragScrollLeft - delta;
      this.clampScrollPosition();
      event.preventDefault();
    },
    onDragEnd() {
      if (!this.isDragging) {
        return;
      }
      this.isDragging = false;
      this.clampScrollPosition();
      this.pauseAutoScrollTemporarily();
    },
    onTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
      this.autoScrollPaused = true;
    },
    onTouchMove() {
      this.clampScrollPosition();
    },
    onTouchEnd() {
      this.touchStartX = null;
      this.clampScrollPosition();
      this.pauseAutoScrollTemporarily();
    },
    scheduleRetry() {
      if (this.pollAttempts >= MAX_POLL_ATTEMPTS) {
        this.loading = false;
        return;
      }

      this.pollAttempts += 1;
      this.pollTimer = setTimeout(() => {
        this.fetchHotDeals(true);
      }, POLL_INTERVAL_MS);
    },
    async fetchHotDeals(isRetry = false) {
      if (!isRetry) {
        this.loading = true;
      }

      try {
        const response = await axios({
          method: 'post',
          url: '/api/v1/homeHotDeals',
          data: {
            country: this.country,
            currency: this.currency,
          },
        });

        const data = response.data || {};
        const items = Array.isArray(data.comparedItems) ? data.comparedItems : [];
        this.cachedPoolSize = data.poolSize || 0;
        this.builtAt = data.builtAt || null;

        if (items.length) {
          this.products = items.slice(0, this.poolSize);
          this.loading = false;
          return;
        }

        if (data.building || this.cachedPoolSize === 0) {
          this.scheduleRetry();
          return;
        }

        this.products = [];
        this.loading = false;
      } catch (error) {
        if (this.pollAttempts < MAX_POLL_ATTEMPTS) {
          this.scheduleRetry();
          return;
        }
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.home-hot-deals {
  margin-top: 0.875rem;
}

.home-hot-deals__hint {
  margin: 0 0 0.625rem;
  font-size: 0.75rem;
  line-height: 1.45;
  color: #64748b;
  text-align: center;
}

[dir='rtl'] .home-hot-deals__hint {
  text-align: right;
}

[dir='ltr'] .home-hot-deals__hint {
  text-align: left;
}

.home-hot-deals__viewport {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: auto;
  scrollbar-width: none;
  cursor: grab;
  touch-action: pan-x;
  margin: 0 -0.25rem;
  padding: 0.25rem 0.25rem 0.5rem;
}

.home-hot-deals__viewport::-webkit-scrollbar {
  display: none;
}

.home-hot-deals__viewport--dragging {
  cursor: grabbing;
  scroll-behavior: auto;
}

.home-hot-deals__viewport--loading {
  overflow: hidden;
  cursor: default;
}

.home-hot-deals__track {
  display: flex;
  gap: 0.75rem;
  width: max-content;
  padding-bottom: 0.125rem;
}

.home-hot-deals__slide {
  flex: 0 0 auto;
  width: min(72vw, 15rem);
}

.home-hot-deals__skeleton,
.home-hot-deals__empty {
  min-height: 17rem;
  border-radius: 1rem;
  border: 1px dashed #cbd5e1;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.72) 0%, rgba(241, 245, 249, 0.9) 100%);
}

.home-hot-deals__skeleton {
  animation: home-hot-deals-pulse 1.4s ease-in-out infinite;
}

.home-hot-deals__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  font-size: 0.875rem;
  color: #64748b;
  text-align: center;
}

.home-hot-deals__slide ::v-deep .product-card {
  height: 100%;
  min-height: 0;
}

.home-hot-deals__slide ::v-deep .product-card--compact .tw-pt-10 {
  padding-top: 2rem;
}

.home-hot-deals__slide ::v-deep .product-card--compact .tw-px-5 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.home-hot-deals__slide ::v-deep .product-card--compact .tw-p-5 {
  padding: 0.75rem;
  gap: 0.625rem;
}

.home-hot-deals__slide ::v-deep .product-card--compact .tw-h-44,
.home-hot-deals__slide ::v-deep .product-card--compact .sm\:tw-h-56 {
  height: 6.5rem;
}

.home-hot-deals__slide ::v-deep .product-card--compact h3 {
  min-height: 2.25rem;
  font-size: 0.8125rem;
  line-height: 1.35;
}

.home-hot-deals__slide ::v-deep .product-card--compact .store-row {
  padding: 0.375rem 0.5rem;
  border-radius: 0.75rem;
}

.home-hot-deals__slide ::v-deep .product-card--compact .store-row__price {
  font-size: 0.875rem;
}

.home-hot-deals__slide ::v-deep .product-card--compact .tw-bg-brand-soft {
  font-size: 0.625rem;
  line-height: 1.3;
  padding: 0.2rem 0.45rem;
}

.home-hot-deals__slide ::v-deep .product-card--compact button.tw-right-3 {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  min-height: 2rem;
}

@keyframes home-hot-deals-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}

@media (min-width: 640px) {
  .home-hot-deals__slide {
    width: 14.5rem;
  }
}

@media (min-width: 1024px) {
  [dir='rtl'] .home-hot-deals__hint {
    text-align: right;
  }

  [dir='ltr'] .home-hot-deals__hint {
    text-align: left;
  }

  .home-hot-deals__viewport {
    margin: 0;
    padding: 0.125rem 0 0.375rem;
  }

  .home-hot-deals__slide {
    width: 15rem;
  }
}
</style>
